import React, { useState } from 'react'
import { Plus, Edit, Trash2, X, Upload, DollarSign, Package, Image as ImageIcon, ChevronUp, ChevronDown } from 'lucide-react'
import { EventFormData, MenuItem } from '../EventCreationWizard'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../../ui/EnterpriseToast'
import DefaultMenuCatalog from '../DefaultMenuCatalog'

interface MenuProductsStepProps {
  formData: EventFormData
  updateFormData: (updates: Partial<EventFormData>) => void
  onNext: () => void
  onPrev: () => void
  isFirstStep: boolean
  isLastStep: boolean
}

interface MenuItemFormData {
  name: string
  price: number
  description: string
  image_url?: string
  inventory: number | null
  lobster_tail_qty: number
}

const MenuProductsStep: React.FC<MenuProductsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}) => {
  const [showModal, setShowModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const [catalogCollapsed, setCatalogCollapsed] = useState(false)
  const [itemForm, setItemForm] = useState<MenuItemFormData>({
    name: '',
    price: 0,
    description: '',
    image_url: undefined,
    inventory: null,
    lobster_tail_qty: 0
  })
  const [isUnlimited, setIsUnlimited] = useState(true)

  const openAddModal = () => {
    setEditingIndex(null)
    setItemForm({
      name: '',
      price: 0,
      description: '',
      image_url: undefined,
      inventory: null,
      lobster_tail_qty: 0
    })
    setIsUnlimited(true)
    setShowModal(true)
  }

  const openEditModal = (index: number) => {
    const item = formData.menuItems[index]
    setEditingIndex(index)
    setItemForm({
      name: item.name,
      price: item.price,
      description: item.description || '',
      image_url: item.image_url,
      inventory: item.inventory,
      lobster_tail_qty: item.lobster_tail_qty || 0
    })
    setIsUnlimited(item.inventory === null)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingIndex(null)
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid File Type"
          message="Please upload an image file"
        />
      ))
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="File Too Large"
          message="Image must be less than 5MB"
        />
      ))
      return
    }

    // Delete old image if replacing
    if (itemForm.image_url) {
      await deleteImageFromStorage(itemForm.image_url)
    }

    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`
      const filePath = `menu-items/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('secret-assets')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('secret-assets')
        .getPublicUrl(filePath)

      setItemForm({ ...itemForm, image_url: publicUrl })
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Image Uploaded"
          message="Menu item image uploaded successfully"
        />
      ))
    } catch (error) {
      console.error('Upload error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Upload Failed"
          message="Failed to upload image. Please try again."
        />
      ))
    } finally {
      setUploading(false)
    }
  }

  const deleteImageFromStorage = async (imageUrl: string) => {
    try {
      const url = new URL(imageUrl)
      const pathParts = url.pathname.split('/storage/v1/object/public/secret-assets/')
      if (pathParts.length > 1) {
        const filePath = decodeURIComponent(pathParts[1])
        const { error } = await supabase.storage
          .from('secret-assets')
          .remove([filePath])

        if (error) {
          console.error('Failed to delete image from storage:', error)
        }
      }
    } catch (error) {
      console.error('Failed to parse image URL for deletion:', error)
    }
  }

  const handleSaveItem = () => {
    // Validation
    if (!itemForm.name.trim()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Name Required"
          message="Product name is required"
        />
      ))
      return
    }

    if (itemForm.price <= 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Price"
          message="Price must be greater than 0"
        />
      ))
      return
    }

    if (!isUnlimited && (itemForm.inventory === null || itemForm.inventory <= 0)) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Inventory"
          message="Inventory must be greater than 0 or set to unlimited"
        />
      ))
      return
    }

    const newItem: MenuItem = {
      id: editingIndex !== null ? formData.menuItems[editingIndex].id : `temp-${Date.now()}`,
      name: itemForm.name,
      price: itemForm.price,
      description: itemForm.description,
      image_url: itemForm.image_url,
      inventory: isUnlimited ? null : itemForm.inventory,
      lobster_tail_qty: itemForm.lobster_tail_qty || 0,
      order_index: editingIndex !== null ? formData.menuItems[editingIndex].order_index : formData.menuItems.length
    }

    let updatedItems = [...formData.menuItems]

    if (editingIndex !== null) {
      updatedItems[editingIndex] = newItem
    } else {
      updatedItems.push(newItem)
    }

    updateFormData({ menuItems: updatedItems })
    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        type="success"
        title={editingIndex !== null ? 'Item Updated' : 'Item Added'}
        message={editingIndex !== null ? 'Menu item updated successfully' : 'Menu item added to the event'}
      />
    ))
    closeModal()
  }

  const handleDeleteItem = (index: number) => {
    if (confirm('Are you sure you want to delete this menu item?')) {
      const updatedItems = formData.menuItems.filter((_, i) => i !== index)
      // Reindex
      updatedItems.forEach((item, i) => {
        item.order_index = i
      })
      updateFormData({ menuItems: updatedItems })
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Item Deleted"
          message="Menu item removed from the event"
        />
      ))
    }
  }

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= formData.menuItems.length) return

    const updatedItems = [...formData.menuItems]
    const temp = updatedItems[index]
    updatedItems[index] = updatedItems[newIndex]
    updatedItems[newIndex] = temp

    // Update order_index
    updatedItems.forEach((item, i) => {
      item.order_index = i
    })

    updateFormData({ menuItems: updatedItems })
  }

  const handleAddCatalogItem = (defaultItem: any) => {
    const newItem: MenuItem = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2)}`,
      name: defaultItem.name,
      price: defaultItem.price,
      description: defaultItem.description,
      image_url: defaultItem.image_url,
      inventory: null, // Unlimited by default
      lobster_tail_qty: defaultItem.lobster_tail_qty || 0,
      order_index: formData.menuItems.length
    }

    updateFormData({ menuItems: [...formData.menuItems, newItem] })

    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        type="success"
        title="Item Added"
        message={`${defaultItem.name} added to menu`}
      />
    ))
  }

  const handleNext = () => {
    if (formData.menuItems.length === 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="No Menu Items"
          message="Please add at least one menu item to continue"
        />
      ))
      return
    }

    onNext()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="menu-item-name text-2xl font-bold mb-2">Menu & Products</h2>
          <p className="menu-item-description">
            Add the items customers can pre-order for this event
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="menu-item-name flex items-center space-x-2 px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
        >
          <Plus size={16} />
          <span>Add Custom Item</span>
        </button>
      </div>

      {/* Default Menu Catalog */}
      <DefaultMenuCatalog
        onAddItem={handleAddCatalogItem}
        isCollapsed={catalogCollapsed && formData.menuItems.length > 0}
        onToggleCollapse={() => setCatalogCollapsed(!catalogCollapsed)}
      />

      {/* Menu Items List */}
      {formData.menuItems.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
          <Package className="menu-item-inventory mx-auto mb-4" size={48} />
          <h3 className="menu-item-name text-lg font-semibold mb-2">No items added yet</h3>
          <p className="menu-item-description mb-4">
            Select items from the catalog above or create a custom item
          </p>
          <button
            onClick={openAddModal}
            className="menu-item-name inline-flex items-center space-x-2 px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
          >
            <Plus size={16} />
            <span>Add Custom Item</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {formData.menuItems.map((item, index) => (
            <div
              key={item.id}
              className="bg-card border border-border rounded-lg p-4 flex items-center space-x-4 hover:border-primary/50 transition-colors"
            >
              {/* Item Image */}
              <div className="flex-shrink-0">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-md border border-border"
                  />
                ) : (
                  <div className="w-20 h-20 bg-muted rounded-md flex items-center justify-center border border-border">
                    <ImageIcon className="text-muted-foreground" size={24} />
                  </div>
                )}
              </div>

              {/* Item Details */}
              <div className="flex-1 min-w-0">
                <h3 className="menu-item-name font-semibold truncate">{item.name}</h3>
                <p className="menu-item-description text-sm line-clamp-1">{item.description || 'No description'}</p>
                <div className="flex items-center space-x-4 mt-1">
                  <span className="menu-item-price text-sm font-medium">
                    ${item.price.toFixed(2)}
                  </span>
                  <span className="menu-item-inventory text-xs">
                    {item.inventory === null ? '∞ Unlimited' : `${item.inventory} available`}
                    {item.lobster_tail_qty > 0 && (
                      <span className="ml-2 text-primary font-medium">
                        • 🦞 {item.lobster_tail_qty} tail{item.lobster_tail_qty !== 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => moveItem(index, 'up')}
                  disabled={index === 0}
                  type="button"
                  className="p-2 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                  title="Move up"
                >
                  <ChevronUp size={20} color="#6b7280" strokeWidth={2} />
                </button>
                <button
                  onClick={() => moveItem(index, 'down')}
                  disabled={index === formData.menuItems.length - 1}
                  type="button"
                  className="p-2 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                  title="Move down"
                >
                  <ChevronDown size={20} color="#6b7280" strokeWidth={2} />
                </button>
                <button
                  onClick={() => openEditModal(index)}
                  type="button"
                  className="p-2 rounded-md transition-colors hover:bg-blue-50"
                  title="Edit"
                >
                  <Edit size={18} color="#2563eb" strokeWidth={2} />
                </button>
                <button
                  onClick={() => handleDeleteItem(index)}
                  type="button"
                  className="p-2 rounded-md transition-colors hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 size={18} color="#dc2626" strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">
                {editingIndex !== null ? 'Edit Menu Item' : 'Add Menu Item'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 hover:bg-muted rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Product Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g., The SECRET Lobster Tail"
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Price <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Brief description of the item..."
                  rows={3}
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {/* Product Image */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Product Image
                </label>
                {itemForm.image_url ? (
                  <div className="relative rounded-lg overflow-hidden border border-border group">
                    <img
                      src={itemForm.image_url}
                      alt="Product"
                      className="w-full h-40 object-cover"
                    />
                    <button
                      onClick={async () => {
                        if (itemForm.image_url) {
                          await deleteImageFromStorage(itemForm.image_url)
                        }
                        setItemForm({ ...itemForm, image_url: undefined })
                      }}
                      className="absolute top-2 right-2 p-1 bg-destructive text-white rounded hover:bg-destructive/90"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-all">
                    {uploading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Upload className="mx-auto mb-2 text-muted-foreground" size={24} />
                        <p className="text-sm text-muted-foreground">Click to upload image</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Inventory */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Inventory <span className="text-destructive">*</span>
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={isUnlimited}
                      onChange={(e) => {
                        setIsUnlimited(e.target.checked)
                        if (e.target.checked) {
                          setItemForm({ ...itemForm, inventory: null })
                        }
                      }}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-foreground">Unlimited inventory</span>
                  </label>
                  {!isUnlimited && (
                    <input
                      type="number"
                      min="1"
                      value={itemForm.inventory || ''}
                      onChange={(e) => setItemForm({ ...itemForm, inventory: parseInt(e.target.value) || null })}
                      placeholder="Enter quantity"
                      className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                </div>
              </div>

              {/* Lobster Tail Quantity */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Lobster Tails per Item <span className="text-destructive">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={itemForm.lobster_tail_qty}
                  onChange={(e) => setItemForm({ ...itemForm, lobster_tail_qty: parseInt(e.target.value) || 0 })}
                  placeholder="0 for add-ons (water, chips)"
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  🦞 How many lobster tails does this item contain? Enter 0 for non-tail items like water, chips, or honey.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end space-x-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveItem}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                {editingIndex !== null ? 'Save Changes' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuProductsStep
